import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import { MACHINE_MAX_GAP_MS, type ScanObservation } from '@/features/scan/wedge';

/**
 * The scanner check: what this browser actually received from the reader in front of it.
 *
 * **It exists because the device is unknown.** Keyboard-wedge readers differ in how fast they type,
 * whether they chunk their payload, whether they send a suffix, whether they decorate the code with
 * a prefix, and whether they type at all rather than inserting or pasting. When a band does not
 * scan, the question is which of those is happening, and without this the only way to answer it is
 * to reproduce the terminal. With it the answer is one scan: the gaps, the characters, the path the
 * bytes took, and whether the codec accepted the result.
 *
 * **It observes; it never changes what the gate does.** There is no mode to leave switched on and
 * nothing here is consulted by the product's own behaviour, so an operator who opens it, scans, and
 * walks away has changed nothing.
 *
 * **It must not be a dialog and must not hold focus.** `scanSuppressed` treats anything inside a
 * `[role="dialog"]` as owning the keyboard, so building this as a modal would suppress the very
 * capture it exists to watch and it would faithfully report that nothing ever arrives. It is a
 * disclosure with a button, and opening it leaves focus on that button.
 */

/** How many arrivals are kept. Two codes' worth, which is all a diagnosis ever needs. */
const TRACE_LIMIT = 48;

/**
 * One arrival with a stable identity.
 *
 * The sequence number exists so the table has a key that is not the array index. A trace is an
 * append-only log of events that are legitimately identical — the same character can arrive twice
 * in one run — so the index is the only other candidate, and silencing the rule that says so would
 * be a strictness downgrade rather than an answer.
 */
export type TracedObservation = ScanObservation & { readonly seq: number };

export interface ScannerCheckProps {
  /** The arrivals to render, oldest first. */
  readonly trace: readonly TracedObservation[];
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onClear: () => void;
}

/**
 * Collects arrivals without re-rendering per keystroke.
 *
 * A code is twenty-one characters inside a fifth of a second, and pushing each one straight into
 * React state would be twenty-one renders inside the scan it is measuring: the one thing a
 * diagnostic must never do is change the behaviour under observation. Arrivals accumulate in a ref
 * and the state is replaced once per run, when a terminal decision closes it, which is one render
 * per scan. The ref is written from the callback and read inside the setter, never during a render.
 */
export function useScannerTrace(): {
  readonly trace: readonly TracedObservation[];
  readonly observe: (observation: ScanObservation) => void;
  readonly clear: () => void;
} {
  const pending = useRef<TracedObservation[]>([]);
  const nextSeq = useRef(0);
  const [trace, setTrace] = useState<readonly TracedObservation[]>([]);

  const observe = useCallback((observation: ScanObservation) => {
    nextSeq.current += 1;
    const next = [...pending.current, { ...observation, seq: nextSeq.current }].slice(-TRACE_LIMIT);
    pending.current = next;
    // A run ends on anything that is not "still collecting", which is exactly the set below.
    if (observation.action !== 'buffer') {
      setTrace(next);
    }
  }, []);

  const clear = useCallback(() => {
    pending.current = [];
    setTrace([]);
  }, []);

  return { trace, observe, clear };
}

/** A gap, or a dash for the first arrival of a run, which has nothing to be measured against. */
function gapLabel(gapMs: number | null): string {
  return gapMs === null ? '—' : String(Math.round(gapMs));
}

export function ScannerCheck({ trace, open, onToggle, onClear }: ScannerCheckProps): ReactNode {
  const { t } = useTranslation();
  const gaps = trace.map((entry) => entry.gapMs).filter((gap): gap is number => gap !== null);
  const symbols = trace.filter((entry) => entry.symbol !== null).length;

  return (
    <section className="rounded-md border bg-surface">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid="scanner-check-toggle"
        aria-expanded={open}
        onClick={onToggle}
      >
        {t('scan.diagnostics.toggle')}
      </Button>

      {open && (
        <div className="space-y-2 px-3 pb-3 text-sm" data-testid="scanner-check">
          <p className="text-muted-foreground">{t('scan.diagnostics.hint')}</p>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
            <dt className="text-muted-foreground">{t('scan.diagnostics.received')}</dt>
            <dd data-testid="scanner-check-received">{trace.length}</dd>
            <dt className="text-muted-foreground">{t('scan.diagnostics.symbols')}</dt>
            <dd>{symbols}</dd>
            <dt className="text-muted-foreground">{t('scan.diagnostics.widestGap')}</dt>
            <dd data-testid="scanner-check-gap">
              {gaps.length === 0 ? '—' : `${String(Math.round(Math.max(...gaps)))} ms`}
            </dd>
            <dt className="text-muted-foreground">{t('scan.diagnostics.cadence')}</dt>
            <dd data-testid="scanner-check-cadence">
              {/*
                This is a PER-GAP reading and it decides nothing, which is what
                {@link MACHINE_MAX_GAP_MS}'s own note says: the attribution is a TOTAL test against
                `MACHINE_MAX_TOTAL_MS`. The two disagree across the whole band a configured reader
                sits in — twenty gaps of 100 ms read "slow" here and are filed `scan_hardware` —
                so the string says what the pause means and states the real rule beside it rather
                than promising an outcome this number does not decide.
              */}
              {gaps.length > 0 && Math.max(...gaps) > MACHINE_MAX_GAP_MS
                ? t('scan.diagnostics.slow')
                : t('scan.diagnostics.brisk')}
            </dd>
          </dl>

          {trace.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-muted-foreground">
                    <th scope="col">{t('scan.diagnostics.key')}</th>
                    <th scope="col">{t('scan.diagnostics.gap')}</th>
                    <th scope="col">{t('scan.diagnostics.symbol')}</th>
                    <th scope="col">{t('scan.diagnostics.via')}</th>
                    <th scope="col">{t('scan.diagnostics.modifier')}</th>
                    <th scope="col">{t('scan.diagnostics.decision')}</th>
                  </tr>
                </thead>
                <tbody>
                  {trace.map((entry) => (
                    <tr key={entry.seq}>
                      <td className="font-mono">{entry.key}</td>
                      <td className="font-mono">{gapLabel(entry.gapMs)}</td>
                      <td className="font-mono">{entry.symbol ?? '—'}</td>
                      <td>{entry.via}</td>
                      <td>{entry.chorded ? t('scan.diagnostics.held') : '—'}</td>
                      <td>{entry.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Button type="button" variant="outline" size="sm" onClick={onClear}>
            {t('scan.diagnostics.clear')}
          </Button>
        </div>
      )}
    </section>
  );
}
